import React from 'react';
import { useHistory } from 'react-router-dom';
import LoadingIndicator from 'src/components/LoadingIndicator';
import MapHeader from 'src/components/MapHeader';
import ShipmentPageHeader from 'src/components/ShipmentPageHeader';
import StatusProgress from 'src/components/StatusProgress';
import emptyImg from 'src/images/empty.svg';
import {
  Button,
  Col,
  Container,
  Icon,
  Row,
  TabContent,
  TabPane,
  useApp
} from 'straightline-ui';
import { differenceInSeconds } from 'straightline-utils/time';

import BOL from './bol';
import { Details, HazmatModal, InternationalModal } from './details';
import Documents from './documents';
import Invoice from './invoice';

export default function Shipment({ match, location }) {
  const app = useApp();
  const history = useHistory();

  const {
    result: shipment,
    isLoading,
    isError,
    error
  } = app.service('api/shipments').useGet(
    match.params.id,
    {},
    {
      refetchInterval: (result) => {
        if (!result) {
          return false;
        }
        // When newly created, there are still dispatches and other side
        // effects happening in the background. There may also be an SL
        // admin user making quick updates as this is created. Ping
        // for updates on new shipments
        const created = differenceInSeconds(new Date(), result.created_at);
        if (created <= 10) {
          return 1000;
        }
        if (created <= 60) {
          return 8000;
        }
        if (created <= 60 * 5) {
          return 15000;
        }
        return 60000;
      }
    }
  );

  const activeTab = location.query?.tab || 'details';
  const activeModal = location.query?.modal;
  const handleCloseModal = () => history.replace(location.pathname);

  if (isLoading) {
    return (
      <div className="d-flex vh-100 align-items-center">
        <LoadingIndicator delay={250} />
      </div>
    );
  }

  if (isError) {
    return (
      <Container>
        <Row className="align-items-center justify-content-center vh-100">
          <Col lg="6" className="text-center">
            <img
              src={emptyImg}
              alt="Not Found"
              className="img-fluid animated fadeIn faster mb-5"
            />
            {error.code === 404 ? (
              <h1>Could not find that shipment</h1>
            ) : (
              <h1>{error.message}</h1>
            )}
            <Button to="/shipments" color="primary" size="lg" className="mr-3">
              <Icon type="package" className="mr-2" />
              Return to Shipments
            </Button>
            <Button to="/support" color="secondary" size="lg">
              <Icon type="send" className="mr-2" />
              Contact Straightline
            </Button>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <div>
      <HazmatModal
        shipment={shipment}
        open={activeModal === 'hazm'}
        toggle={handleCloseModal}
        onComplete={handleCloseModal}
      />
      <InternationalModal
        shipment={shipment}
        open={activeModal === 'intl'}
        toggle={handleCloseModal}
        onComplete={handleCloseModal}
      />
      <MapHeader shipment={shipment} />
      <StatusProgress
        shipment={shipment}
        unshift={window.innerWidth < 1050}
        trimFlat={true}
        unshiftFlat={true}
      />
      <Container fluid className="pb-5">
        <ShipmentPageHeader shipment={shipment} activeTab={activeTab} />
        <TabContent activeTab={activeTab}>
          <TabPane tabId="details">
            <Details shipment={shipment} />
          </TabPane>
          <TabPane tabId="bol">
            <BOL shipment={shipment} />
          </TabPane>
          <TabPane tabId="invoice">
            <Invoice shipment={shipment} />
          </TabPane>
          <TabPane tabId="documents">
            <Documents shipment={shipment} />
          </TabPane>
        </TabContent>
      </Container>
    </div>
  );
}
